import {
    type InjectionKey,
    ref,
    inject,
    computed,
    provide,
    type Ref,
    type ComputedRef,
    onMounted,
    onUnmounted,
    MaybeRefOrGetter,
    toValue,
} from 'vue';
import { eagerComputed, useEventListener } from '@vueuse/core';
import {
    BoundingBox,
    Position,
    boundingBoxContains,
} from '../helpers/positions';
import {
    type NodeRepr,
    reprMaybeGetPath,
    isSubPathOf,
    reprSwapPath,
} from '../helpers/internal-repr';

export type DragController<T = string> = {
    draggedReprPath: Ref<number[] | null>;
    draggedRepr: ComputedRef<NodeRepr<T> | undefined>;
    isDraggedPath(path: number[]): boolean;
    containsDraggedPath(path: number[]): boolean;
    isDragging: ComputedRef<boolean>;
    dragPos: Ref<Position | null>;
    onDrop: (listener: DropListener<T>) => () => void;
    offDrop: (listener: DropListener<T>) => void;
    startDragging(path: number[], event: MouseEvent): void;
};

export type DragControllerInput<T = string> =
    | string
    | symbol
    | DragController<T>
    | InjectionKey<DragController<T>>
    | undefined
    | null;

export type DropListener<T = string> = (
    droppedPath: number[],
    droppedRepr: NodeRepr<T>,
    event: MouseEvent,
    swap: (path: number[]) => void
) => void;

const dragControllerInjectionKey = Symbol(
    'expr-node-repr-drag-controller'
) as InjectionKey<DragController<any>>;

function createDragController<T = string>(
    root: Ref<NodeRepr<T>>
): DragController<T> {
    const draggedReprPath = ref<null | number[]>(null);
    const draggedRepr = computed(() => {
        if (!draggedReprPath.value) {
            return undefined;
        } else {
            return reprMaybeGetPath(root.value, draggedReprPath.value);
        }
    });
    function isDraggedPath(path: number[]): boolean {
        if (!draggedReprPath.value) return false;
        return isSubPathOf(draggedReprPath.value, path);
    }
    function containsDraggedPath(path: number[]): boolean {
        if (!draggedReprPath.value) return false;
        return isSubPathOf(path, draggedReprPath.value);
    }
    const isDragging = computed(() => draggedReprPath.value !== null);
    const dragPos = ref<Position | null>(null);

    const dropListeners = new Set<DropListener<T>>();

    function offDrop(listener: DropListener<T>) {
        dropListeners.delete(listener);
    }

    function onDrop(listener: DropListener<T>) {
        dropListeners.add(listener);
        return () => offDrop(listener);
    }

    function startDragging(reprPath: number[], event: MouseEvent) {
        draggedReprPath.value = reprPath;
        dragPos.value = {
            x: event.clientX,
            y: event.clientY,
        };
        event.preventDefault();
    }

    function swapWithDragged(reprPath: number[]) {
        if (!draggedReprPath.value) return;
        root.value = reprSwapPath(root.value, draggedReprPath.value, reprPath);
    }

    useEventListener('pointerup', (event: MouseEvent) => {
        const path = draggedReprPath.value;
        const repr = draggedRepr.value;
        if (path && repr) {
            for (const listener of dropListeners.values()) {
                listener(path, repr, event, swapWithDragged);
            }
        }
        dragPos.value = null;
        draggedReprPath.value = null;
    });

    useEventListener('pointermove', (event: MouseEvent) => {
        if (dragPos.value) {
            dragPos.value = {
                x: event.clientX,
                y: event.clientY,
            };
        }
    });

    return {
        draggedReprPath,
        draggedRepr,
        startDragging,
        isDraggedPath,
        containsDraggedPath,
        isDragging,
        dragPos,
        onDrop,
        offDrop,
    };
}

export function useDragController<T = string>(
    root: Ref<NodeRepr<T>>
): DragController<T> {
    const injectedController = inject<DragController<T> | null>(
        dragControllerInjectionKey,
        null
    );

    if (!injectedController) {
        const resp = createDragController<T>(root);
        provide(dragControllerInjectionKey, resp);
        return resp;
    } else {
        return injectedController;
    }
}

export function useDraggableNodeRepr<T = string>(
    repr: Ref<NodeRepr<T>>,
    path: MaybeRefOrGetter<number[]>
) {
    const {
        startDragging: _startDragging,
        isDraggedPath,
        containsDraggedPath,
        isDragging,
    } = useDragController<T>(repr);

    function startDragging(event: MouseEvent) {
        _startDragging(toValue(path), event);
    }

    const parentDragging = eagerComputed(() => {
        return isDraggedPath(toValue(path));
    });

    const childDragging = eagerComputed(() => {
        return containsDraggedPath(toValue(path));
    });
    const dragging = eagerComputed(
        () => parentDragging.value && childDragging.value
    );

    return {
        startDragging,
        dragging,
        isDragging,
        parentDragging,
        childDragging,
    };
}

export type ReprDropZoneOptions = {
    boundingBox: () => BoundingBox | false | null | undefined | void;
    disabled?: MaybeRefOrGetter<boolean>;
    path: MaybeRefOrGetter<number[]>;
};

export function useReprDropZone(options: ReprDropZoneOptions) {
    const dragController = inject(dragControllerInjectionKey);

    if (!dragController) {
        throw new Error('useReprDropZone without injected DragController.');
    }

    const { onDrop, offDrop, dragPos, isDragging, isDraggedPath } =
        dragController;

    const disabled = eagerComputed(() => !!toValue(options.disabled));

    const active = eagerComputed(() => {
        if (disabled.value) return false;
        if (!isDragging.value) return false;
        if (isDraggedPath(toValue(options.path))) return false;
        return true;
    });

    function dropListener(
        droppedPath: number[],
        repr: NodeRepr<unknown>,
        event: MouseEvent,
        swap: (path: number[]) => void
    ) {
        if (!active.value) return;

        const boundingBox = options.boundingBox();
        if (!boundingBox) return;

        const dropPos: Position = {
            x: event.clientX,
            y: event.clientY,
        };

        if (!boundingBoxContains(boundingBox, dropPos)) return;

        swap(toValue(options.path));
    }

    onMounted(() => {
        onDrop(dropListener);
    });

    onUnmounted(() => {
        offDrop(dropListener);
    });

    const hovering = computed(() => {
        if (!active.value) return false;

        const pos = dragPos.value;
        if (!pos) return false;

        const boundingBox = options.boundingBox();
        if (!boundingBox) return false;

        return boundingBoxContains(boundingBox, pos);
    });

    return {
        active,
        hovering,
    };
}
